/**
 * Thomas Alan Style - Shopify Theme JavaScript
 * Elevated Menswear & Lifestyle
 */

(function() {
  'use strict';

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================
  
  const debounce = (fn, wait) => {
    let timeout;
    return function(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
  };

  const fetchConfig = (type = 'json') => {
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': `application/${type}`
      }
    };
  };

  // ============================================
  // HEADER
  // ============================================
  
  class Header {
    constructor() {
      this.header = document.querySelector('.header');
      this.menuToggle = document.querySelector('.header__menu-toggle');
      this.mobileMenu = document.querySelector('.mobile-menu');
      this.mobileMenuClose = document.querySelector('.mobile-menu__close');
      
      this.lastScroll = 0;
      this.init();
    }

    init() {
      if (this.menuToggle) {
        this.menuToggle.addEventListener('click', () => this.openMobileMenu());
      }
      
      if (this.mobileMenuClose) {
        this.mobileMenuClose.addEventListener('click', () => this.closeMobileMenu());
      }

      // Close mobile menu on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.mobileMenu?.classList.contains('active')) {
          this.closeMobileMenu();
        }
      });

      // Header scroll behavior
      window.addEventListener('scroll', debounce(() => this.handleScroll(), 10));
    }

    openMobileMenu() {
      this.mobileMenu?.classList.add('active');
      document.body.style.overflow = 'hidden';
    }

    closeMobileMenu() {
      this.mobileMenu?.classList.remove('active');
      document.body.style.overflow = '';
    }

    handleScroll() {
      const currentScroll = window.scrollY;
      
      if (currentScroll > 100) {
        this.header?.classList.add('header--scrolled');
      } else {
        this.header?.classList.remove('header--scrolled');
      }

      this.lastScroll = currentScroll;
    }
  }

  // ============================================
  // PRODUCT FORM
  // ============================================
  
  class ProductForm {
    constructor(form) {
      this.form = form;
      this.submitButton = form.querySelector('[type="submit"]');
      this.variantSelects = form.querySelectorAll('[data-variant-option]');
      this.quantityInput = form.querySelector('[name="quantity"]');
      this.quantityBtns = form.querySelectorAll('.product__quantity-btn');
      
      this.init();
    }

    init() {
      this.form.addEventListener('submit', (e) => this.handleSubmit(e));
      
      this.variantSelects.forEach(select => {
        select.addEventListener('change', () => this.handleVariantChange());
      });

      this.quantityBtns.forEach(btn => {
        btn.addEventListener('click', (e) => this.handleQuantityChange(e));
      });

      // Handle option buttons (non-select variant options)
      const optionButtons = this.form.querySelectorAll('.product__option-value');
      optionButtons.forEach(btn => {
        btn.addEventListener('click', (e) => this.handleOptionClick(e));
      });
    }

    handleOptionClick(e) {
      const button = e.currentTarget;
      const option = button.closest('.product__option');
      
      option.querySelectorAll('.product__option-value').forEach(btn => {
        btn.classList.remove('selected');
      });
      
      button.classList.add('selected');
      this.handleVariantChange();
    }

    handleVariantChange() {
      const selectedOptions = [];
      
      this.form.querySelectorAll('.product__option').forEach(option => {
        const selected = option.querySelector('.product__option-value.selected');
        if (selected) {
          selectedOptions.push(selected.dataset.value);
        }
      });

      // Find matching variant
      const variantId = this.findVariant(selectedOptions);
      if (variantId) {
        const variantInput = this.form.querySelector('[name="id"]');
        if (variantInput) variantInput.value = variantId;
      }
    }

    findVariant(options) {
      const productData = this.form.dataset.productJson;
      if (!productData) return null;

      try {
        const product = JSON.parse(productData);
        const variant = product.variants.find(v => {
          return options.every((opt, index) => v[`option${index + 1}`] === opt);
        });
        return variant?.id;
      } catch (e) {
        console.error('Error parsing product data:', e);
        return null;
      }
    }

    handleQuantityChange(e) {
      const btn = e.currentTarget;
      const action = btn.dataset.action;
      let currentValue = parseInt(this.quantityInput.value) || 1;

      if (action === 'increase') {
        currentValue++;
      } else if (action === 'decrease' && currentValue > 1) {
        currentValue--;
      }

      this.quantityInput.value = currentValue;
    }

    async handleSubmit(e) {
      e.preventDefault();
      
      this.submitButton.disabled = true;
      this.submitButton.classList.add('loading');
      
      const formData = new FormData(this.form);
      
      try {
        const response = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          body: formData
        });

        const data = await response.json();
        
        if (data.status === 422) {
          throw new Error(data.description || 'Error adding to cart');
        }

        // Update cart count
        this.updateCartCount();
        
        // Show success feedback
        this.showAddedFeedback();
        
      } catch (error) {
        console.error('Error:', error);
        alert(error.message);
      } finally {
        this.submitButton.disabled = false;
        this.submitButton.classList.remove('loading');
      }
    }

    async updateCartCount() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        
        const cartCounts = document.querySelectorAll('.header__cart-count');
        cartCounts.forEach(el => {
          el.textContent = cart.item_count;
          el.style.display = cart.item_count > 0 ? 'flex' : 'none';
        });
      } catch (error) {
        console.error('Error updating cart count:', error);
      }
    }

    showAddedFeedback() {
      const originalText = this.submitButton.textContent;
      this.submitButton.textContent = 'Added!';
      
      setTimeout(() => {
        this.submitButton.textContent = originalText;
      }, 2000);
    }
  }

  // ============================================
  // PRODUCT GALLERY
  // ============================================
  
  class ProductGallery {
    constructor(gallery) {
      this.gallery = gallery;
      this.mainImage = gallery.querySelector('.product__main-image img');
      this.thumbnails = gallery.querySelectorAll('.product__thumbnail');
      
      this.init();
    }

    init() {
      this.thumbnails.forEach(thumb => {
        thumb.addEventListener('click', () => this.handleThumbnailClick(thumb));
      });
    }

    handleThumbnailClick(thumb) {
      const newSrc = thumb.dataset.fullSize || thumb.querySelector('img').src;
      
      this.thumbnails.forEach(t => t.classList.remove('active'));
      thumb.classList.add('active');
      
      this.mainImage.src = newSrc;
    }
  }

  // ============================================
  // CART
  // ============================================
  
  class Cart {
    constructor() {
      this.cartItems = document.querySelectorAll('.cart__item');
      this.init();
    }

    init() {
      this.cartItems.forEach(item => {
        const removeBtn = item.querySelector('.cart__item-remove');
        const quantityBtns = item.querySelectorAll('.product__quantity-btn');
        const quantityInput = item.querySelector('.product__quantity-input');

        if (removeBtn) {
          removeBtn.addEventListener('click', () => this.removeItem(item));
        }

        quantityBtns.forEach(btn => {
          btn.addEventListener('click', () => this.updateQuantity(item, btn.dataset.action));
        });

        if (quantityInput) {
          quantityInput.addEventListener('change', () => this.updateQuantity(item));
        }
      });
    }

    async removeItem(item) {
      const lineKey = item.dataset.lineKey;
      
      try {
        await this.updateCart({
          id: lineKey,
          quantity: 0
        });
        
        item.remove();
        this.updateCartCount();
        
        // Check if cart is empty
        if (document.querySelectorAll('.cart__item').length === 0) {
          location.reload();
        }
      } catch (error) {
        console.error('Error removing item:', error);
      }
    }

    async updateQuantity(item, action) {
      const input = item.querySelector('.product__quantity-input');
      const lineKey = item.dataset.lineKey;
      let quantity = parseInt(input.value) || 1;

      if (action === 'increase') {
        quantity++;
      } else if (action === 'decrease' && quantity > 1) {
        quantity--;
      }

      input.value = quantity;

      try {
        await this.updateCart({
          id: lineKey,
          quantity: quantity
        });
        
        this.updateCartCount();
        this.updateLineTotal(item, quantity);
      } catch (error) {
        console.error('Error updating quantity:', error);
      }
    }

    async updateCart(updates) {
      const response = await fetch(window.routes.cart_change_url, {
        ...fetchConfig(),
        body: JSON.stringify(updates)
      });
      
      return response.json();
    }

    async updateCartCount() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        
        const cartCounts = document.querySelectorAll('.header__cart-count');
        cartCounts.forEach(el => {
          el.textContent = cart.item_count;
          el.style.display = cart.item_count > 0 ? 'flex' : 'none';
        });

        // Update subtotal
        const subtotal = document.querySelector('.cart__subtotal-price');
        if (subtotal) {
          subtotal.textContent = this.formatMoney(cart.total_price);
        }
      } catch (error) {
        console.error('Error updating cart count:', error);
      }
    }

    updateLineTotal(item, quantity) {
      const priceEl = item.querySelector('.cart__item-price');
      const totalEl = item.querySelector('.cart__item-total');
      
      if (priceEl && totalEl) {
        const price = parseInt(priceEl.dataset.price);
        totalEl.textContent = this.formatMoney(price * quantity);
      }
    }

    formatMoney(cents) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  // ============================================
  // ACCORDION
  // ============================================
  
  class Accordion {
    constructor(accordion) {
      this.accordion = accordion;
      this.trigger = accordion.querySelector('.product__accordion-trigger');
      
      this.init();
    }

    init() {
      this.trigger?.addEventListener('click', () => this.toggle());
    }

    toggle() {
      this.accordion.classList.toggle('active');
    }
  }

  // ============================================
  // NEWSLETTER FORM
  // ============================================
  
  class NewsletterForm {
    constructor(form) {
      this.form = form;
      this.input = form.querySelector('input[type="email"]');
      this.button = form.querySelector('button');
      
      this.init();
    }

    init() {
      this.form.addEventListener('submit', (e) => this.handleSubmit(e));
    }

    async handleSubmit(e) {
      e.preventDefault();
      
      const email = this.input.value;
      
      if (!this.validateEmail(email)) {
        this.showError('Please enter a valid email address');
        return;
      }

      this.button.disabled = true;
      this.button.textContent = 'Subscribing...';

      // Submit to Shopify customer API
      try {
        const response = await fetch('/contact#contact_form', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({
            'form_type': 'customer',
            'utf8': '✓',
            'customer[email]': email,
            'customer[tags]': 'newsletter'
          })
        });

        this.showSuccess('Thank you for subscribing!');
        this.input.value = '';
        
      } catch (error) {
        this.showError('Something went wrong. Please try again.');
      } finally {
        this.button.disabled = false;
        this.button.textContent = 'Subscribe';
      }
    }

    validateEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }

    showSuccess(message) {
      this.showMessage(message, 'success');
    }

    showError(message) {
      this.showMessage(message, 'error');
    }

    showMessage(message, type) {
      const existingMsg = this.form.querySelector('.newsletter__message');
      if (existingMsg) existingMsg.remove();

      const msg = document.createElement('p');
      msg.className = `newsletter__message newsletter__message--${type}`;
      msg.textContent = message;
      msg.style.cssText = `
        margin-top: 12px;
        font-size: 14px;
        color: ${type === 'error' ? '#B54B4B' : '#4BB543'};
      `;
      
      this.form.appendChild(msg);

      setTimeout(() => msg.remove(), 5000);
    }
  }

  // ============================================
  // QUICK ADD
  // ============================================
  
  class QuickAdd {
    constructor(button) {
      this.button = button;
      this.init();
    }

    init() {
      this.button.addEventListener('click', (e) => this.handleClick(e));
    }

    async handleClick(e) {
      e.preventDefault();
      e.stopPropagation();
      
      const variantId = this.button.dataset.variantId;
      
      if (!variantId) {
        // If product has variants, redirect to product page
        const productUrl = this.button.dataset.productUrl;
        if (productUrl) window.location.href = productUrl;
        return;
      }

      this.button.disabled = true;
      const originalText = this.button.textContent;
      this.button.textContent = 'Adding...';

      try {
        const response = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: variantId,
            quantity: 1
          })
        });

        const data = await response.json();

        if (data.status === 422) {
          throw new Error(data.description || 'Error adding to cart');
        }

        this.button.textContent = 'Added!';
        this.updateCartCount();

        setTimeout(() => {
          this.button.textContent = originalText;
        }, 2000);

      } catch (error) {
        console.error('Error:', error);
        this.button.textContent = 'Error';
        
        setTimeout(() => {
          this.button.textContent = originalText;
        }, 2000);
      } finally {
        this.button.disabled = false;
      }
    }

    async updateCartCount() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        
        const cartCounts = document.querySelectorAll('.header__cart-count');
        cartCounts.forEach(el => {
          el.textContent = cart.item_count;
          el.style.display = cart.item_count > 0 ? 'flex' : 'none';
        });
      } catch (error) {
        console.error('Error updating cart count:', error);
      }
    }
  }

  // ============================================
  // INITIALIZATION
  // ============================================
  
  document.addEventListener('DOMContentLoaded', () => {
    // Initialize Header
    new Header();

    // Initialize Product Forms
    document.querySelectorAll('.product__form').forEach(form => {
      new ProductForm(form);
    });

    // Initialize Product Galleries
    document.querySelectorAll('.product__gallery').forEach(gallery => {
      new ProductGallery(gallery);
    });

    // Initialize Cart
    if (document.querySelector('.cart__item')) {
      new Cart();
    }

    // Initialize Accordions
    document.querySelectorAll('.product__accordion').forEach(accordion => {
      new Accordion(accordion);
    });

    // Initialize Newsletter Forms
    document.querySelectorAll('.newsletter__form').forEach(form => {
      new NewsletterForm(form);
    });

    // Initialize Quick Add Buttons
    document.querySelectorAll('.quick-add-btn').forEach(button => {
      new QuickAdd(button);
    });

    // Lazy load images
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
            }
            imageObserver.unobserve(img);
          }
        });
      });

      document.querySelectorAll('img[data-src]').forEach(img => {
        imageObserver.observe(img);
      });
    }
  });

})();
